import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Heart, HeartOff } from 'lucide-react';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState(() => {
    try {
      const savedWishlist = localStorage.getItem('vedhyas_wishlist');
      return savedWishlist ? JSON.parse(savedWishlist) : [];
    } catch (error) {
      console.error('Error reading vedhyas_wishlist from localStorage:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('vedhyas_wishlist', JSON.stringify(wishlist));
    } catch (error) {
      console.error('Error saving vedhyas_wishlist to localStorage:', error);
    }
  }, [wishlist]);

  // Toggle item in/out of wishlist with luxury toast feedback
  const toggleWishlist = (product) => {
    setWishlist((prevWishlist) => {
      const exists = prevWishlist.some((item) => item.id === product.id);

      if (exists) {
        // Notification for removal
        toast.custom(
          (t) => (
            <div
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } max-w-sm w-full bg-[#20050A] text-[#FDFBF7] shadow-2xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-[#4A0E17]`}
            >
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-[#4A0E17] text-[#E8DFC8] rounded-xl border border-[#E8DFC8]/20">
                  <HeartOff className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-serif font-bold text-[#FDFBF7] line-clamp-1">
                    {product.name}
                  </p>
                  <p className="text-[10px] text-[#E8DFC8]/80 font-sans mt-0.5">
                    Removed from saved treasures
                  </p>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-[10px] font-serif font-bold text-[#E8DFC8] hover:text-[#FDFBF7] px-2 py-1 uppercase tracking-widest transition-colors"
              >
                Close
              </button>
            </div>
          ),
          { duration: 2200 }
        );

        return prevWishlist.filter((item) => item.id !== product.id);
      } else {
        // Notification for addition
        toast.custom(
          (t) => (
            <div
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } max-w-sm w-full bg-[#20050A] text-[#FDFBF7] shadow-2xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-[#4A0E17]`}
            >
              <div className="flex items-center space-x-3">
                <div className="p-2.5 bg-[#4A0E17] text-[#E8DFC8] rounded-xl border border-[#E8DFC8]/20">
                  <Heart className="w-4 h-4 fill-current text-[#E8DFC8]" />
                </div>
                <div className="text-left">
                  <p className="text-xs font-serif font-bold text-[#FDFBF7] line-clamp-1">
                    {product.name}
                  </p>
                  <p className="text-[10px] text-[#E8DFC8]/80 font-sans mt-0.5">
                    Saved to your Vedhyas wishlist
                  </p>
                </div>
              </div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="text-[10px] font-serif font-bold text-[#E8DFC8] hover:text-[#FDFBF7] px-2 py-1 uppercase tracking-widest transition-colors"
              >
                Close
              </button>
            </div>
          ),
          { duration: 2200 }
        );

        return [...prevWishlist, product];
      }
    });
  };

  // Explicit remove method
  const removeFromWishlist = (productId) => {
    setWishlist((prevWishlist) => prevWishlist.filter((item) => item.id !== productId));
  };

  // Check if specific product is wishlisted
  const isInWishlist = (productId) => {
    return wishlist.some((item) => item.id === productId);
  };

  // Clear entire wishlist
  const clearWishlist = () => {
    setWishlist([]);
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlist,
        toggleWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};